import { useMemo } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { allProducts, categoryLinks, categoryToKey } from '@/mocks/products';
import ProductCard from '@/components/base/ProductCard';

export default function ProductPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const product = useMemo(() => allProducts.find((p) => p.id === id), [id]);

  const relatedProducts = useMemo(() => {
    if (!product) return [];
    return allProducts
      .filter((p) => p.category === product.category && p.id !== product.id)
      .slice(0, 4);
  }, [product]);

  const currentLang = location.pathname.split('/')[1] || 'en';

  if (!product) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center px-4">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
              {t('productDetail.notFound')}
            </h1>
            <p className="text-foreground-600 mb-6">{t('productDetail.notFoundDesc')}</p>
            <button
              onClick={() => navigate(`/${currentLang}/`)}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors"
            >
              <i className="ri-arrow-left-line" />
              {t('productDetail.backHome')}
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main>
        {/* Breadcrumb */}
        <div className="bg-background-100 border-b border-background-200/60">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4">
            <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 flex-wrap">
              <Link to={`/${currentLang}/`} className="hover:text-primary-600 transition-colors">
                {t('nav.home')}
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-400" />
              <span className="text-foreground-700">
                {t(`categories.${categoryToKey[product.category]}`)}
              </span>
              <i className="ri-arrow-right-s-line text-foreground-400 hidden sm:inline" />
              <span className="text-foreground-950 hidden sm:inline truncate max-w-[200px] md:max-w-sm">
                {product.name}
              </span>
            </nav>
          </div>
        </div>

        {/* Product Detail */}
        <section className="py-8 md:py-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
              {/* Image */}
              <div className="rounded-2xl overflow-hidden bg-background-100 border border-background-200/60">
                <div className="aspect-square">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex flex-col justify-center">
                <span className="inline-flex w-fit items-center gap-1.5 px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium mb-4">
                  <i className="ri-price-tag-3-line" />
                  {t(`categories.${categoryToKey[product.category]}`)}
                </span>

                <h1 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-foreground-950 mb-4 leading-snug">
                  {product.name}
                </h1>

                <div className="flex items-baseline gap-3 mb-6">
                  <span className="text-2xl md:text-3xl font-bold text-primary-600">
                    ¥{product.priceYuan}
                  </span>
                  <span className="text-sm md:text-base text-foreground-500">
                    ≈ ${product.priceUsd.toFixed(2)} USD
                  </span>
                </div>

                <div className="space-y-3 mb-8">
                  <div className="flex items-center gap-3 text-sm text-foreground-600">
                    <i className="ri-check-line text-accent-500" />
                    <span>{t('productDetail.verified')}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-foreground-600">
                    <i className="ri-check-line text-accent-500" />
                    <span>{t('productDetail.qcAvailable')}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-foreground-600">
                    <i className="ri-check-line text-accent-500" />
                    <span>{t('productDetail.shippingGlobal')}</span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 mb-6">
                  <a
                    href={product.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-shopping-cart-line" />
                    {t('productDetail.buyNow')}
                  </a>
                  <a
                    href={categoryLinks[product.category]}
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-background-200 text-foreground-800 text-sm font-medium hover:bg-background-300 transition-colors border border-background-300/60 whitespace-nowrap"
                  >
                    <i className="ri-grid-line" />
                    {t('productDetail.viewCategory')}
                  </a>
                </div>

                <p className="text-xs text-foreground-400">
                  {t('productDetail.redirectNotice')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <section className="py-10 md:py-16 bg-background-100 border-t border-background-200/40">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6 md:mb-8">
                {t('productDetail.relatedTitle')}
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                {relatedProducts.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}